import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { ClipboardList, MessageCircle, Trophy, Briefcase } from "lucide-react";

const HowItWorksSection = () => {
  const steps = [
    {
      icon: <ClipboardList size={40} />,
      title: "Post a Code Issue",
      desc: "Describe your coding problem, add screenshots or code, and set your reward or coins.",
    },
    {
      icon: <MessageCircle size={40} />,
      title: "Get Solutions",
      desc: "Developers around the world submit solutions, fixes, and explanations to your problem.",
    },
    {
      icon: <Trophy size={40} />,
      title: "Reward & Rank Up",
      desc: "Select the best solution, reward the solver, and both of you earn coins and credibility.",
    },
    {
      icon: <Briefcase size={40} />,
      title: "Grow & Get Hired",
      desc: "Top solvers get noticed by companies and can land freelance or referral opportunities.",
    },
  ];

  return (
    <SectionWrapper id="how-it-works">
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        How It Works — <Highlight>Simple, Fast & Rewarding</Highlight>
      </motion.h2>

      <StepsContainer>
        {steps.map((step, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.2 }}
          >
            <StepCard>
              <IconWrapper>{step.icon}</IconWrapper>
              <StepTitle>{step.title}</StepTitle>
              <StepDesc>{step.desc}</StepDesc>
            </StepCard>
          </motion.div>
        ))}
      </StepsContainer>
    </SectionWrapper>
  );
};

export default HowItWorksSection;

// ---------------- Styled Components ----------------

const SectionWrapper = styled.section`
  width: 100%;
  min-height: 60vh;
  background: radial-gradient(circle at top left, #0b0617, #000);
  padding: 80px 20px;
  text-align: center;
  color: #fff;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const StepsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  margin-top: 60px;
`;

const StepCard = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  width: 260px;
  min-height: 220px;
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: 0.3s;
  cursor: default;
  backdrop-filter: blur(6px);

  &:hover {
    transform: translateY(-10px) scale(1.03);
    border-color: #c59fff;
    box-shadow: 0 8px 20px rgba(155, 92, 255, 0.3);
  }
`;

const IconWrapper = styled.div`
  background: rgba(155, 92, 255, 0.15);
  border-radius: 50%;
  padding: 12px;
  margin-bottom: 20px;
  color: #c59fff;
`;

const StepTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 10px;
  color: #c59fff;
`;

const StepDesc = styled.p`
  font-size: 0.95rem;
  color: #eaeaea;
  line-height: 1.5;
`;
